import type { JellyVisualDefinition } from "../../generated/content-types";
import type { GameContext } from "../core/GameContext";
import type { TestLevelFixture } from "../core/types";

export interface JellyAsset {
  parameters: JellyVisualDefinition;
  source: string;
}

// 能力位占 0-7，运行状态位占 8 起，两者不重叠。旧循环的能力位随决策 0016 移除；
// 压力爆浆的能力位由 T045 起按需加入，届时 definition.behaviors 才会有非零值。
// Falling 状态位随锚点坍塌一并移除：新模型只有一种下落，不存在「下压」与「掉落」两态。
export const SlimeState = {
  MarkedDead: 1 << 9,
  /** 已越过红线：只计一次漏网，之后一路落出画面回收。 */
  Leaked: 1 << 10,
} as const;

export type RulePhase =
  | "levelStart"
  | "beforePhysics"
  | "afterPhysics"
  | "afterResolution"
  | "afterFlush"
  | "levelEnd";
export type RuleExecutor = (
  context: GameContext,
  params: Readonly<Record<string, unknown>>,
) => void;

export interface CompiledRule {
  instanceId: string;
  type: string;
  phase: RulePhase;
  params: Readonly<Record<string, unknown>>;
  execute: RuleExecutor;
}

export interface RuleRegistration {
  phase: RulePhase;
  execute: RuleExecutor;
}

export type RuleRegistry = ReadonlyMap<string, RuleRegistration>;

export interface CompiledSlimeDefinition {
  id: string;
  displayName: string;
  /** 静止体积时的半径。半径是体积的函数，不再是常量——见 mechanics/pressure。 */
  radiusRest: number;
  /** 充盈度为 1 时的半径。 */
  radiusFull: number;
  /** 爆裂体积。到达即临界，爆浆由 T046 实现。 */
  burstVolume: number;
  spring: number;
  damping: number;
  /** 有效质量倍率与定义级落速倍率，语义见 slime.schema.json。 */
  mass: number;
  fallScale: number;
  /** 六个钩子里除爆裂阈值（burstVolume）之外的五个，全部是相对基础种的倍率。 */
  absorb: number;
  splashFraction: number;
  splashRadius: number;
  ringWeight: number;
  stickiness: number;
  behaviors: number;
  jelly: JellyAsset;
  visualColor: number;
}

export interface CompiledDropDefinition {
  id: string;
  displayName: string;
  radius: number;
  speed: number;
  behaviors: number;
  visualColor: number;
  pulse: number;
}

export interface CompiledWorld {
  id: string;
  width: number;
  height: number;
  gravity: number;
  /** 竖直阻尼底数，终端速度 = gravity / -ln(fallDamping)。 */
  fallDamping: number;
  /** 横向阻尼底数。原型中它是环境参数而非每种史莱姆的属性。 */
  horizontalDrag: number;
  drop: number;
  fireInterval: number;
  /** 喷射角抖动幅度（弧度）。T054 起由 world.json 提供，此前是代码常量 SPRAY_JITTER。 */
  sprayJitter: number;
  /** 压力模型的世界级参数，语义见 world.schema.json 的 description。 */
  waterRate: number;
  waterImpulse: number;
  liftFactor: number;
  baseFraction: number;
  leakRate: number;
  compression: number;
  repulsion: number;
  /** 贴墙计入的接触深度。T054 起由 world.json 提供，此前是代码常量 WALL_CONTACT_DEPTH。 */
  wallContactDepth: number;
  /** 爆浆参数，语义见 world.schema.json 的 description。 */
  ampCoef: number;
  ampCap: number;
  ringWindow: number;
  splashFraction: number;
  splashRadius: number;
  splashPush: number;
  /** 黏连参数，语义见 world.schema.json 的 description。 */
  bondLife: number;
  bondStrength: number;
  bondSeal: number;
  bondCooldown: number;
  /** 地面带几何，语义见 world.schema.json 的 description。 */
  leakLine: number;
  playerRow: number;
  rules: readonly CompiledRule[];
}

export interface CompiledEntitySpawn {
  instanceId: string;
  type: number;
  x: number;
  y: number;
}

export interface CompiledLevel {
  id: string;
  order: number;
  name: string;
  kicker: string;
  objective: string;
  world: CompiledWorld;
  entities: readonly CompiledEntitySpawn[];
  rules: Readonly<Record<RulePhase, readonly CompiledRule[]>>;
  testFixture: boolean;
}

export interface ContentCatalog {
  slimes: readonly CompiledSlimeDefinition[];
  drops: readonly CompiledDropDefinition[];
  levels: readonly CompiledLevel[];
  slimeIds: ReadonlyMap<string, number>;
  dropIds: ReadonlyMap<string, number>;
  compileFixture(fixture: TestLevelFixture): CompiledLevel;
}
